import { shrpaApi } from "@/repos/axios";
import { getCalendarId } from "@logic/calendarManager";
import type {
  FacebookPageSearchResult,
  InstagramValidationResult,
  LocationData,
  PostLocationDisplayItem,
  SaveLocationRequest,
  TaggableLocation,
  WebsiteSocialDiscoveryResult,
} from "@contracts/socialLocations";

const locationCache = new Map<string, Promise<LocationData[]>>();
const postLocationDisplayCache = new Map<string, Promise<PostLocationDisplayItem[]>>();

function customerKey(customerId: string, includeDeleted: boolean): string {
  return `${customerId.trim().toLowerCase()}:${includeDeleted}`;
}

function postLocationCacheKey(customerId: string, postId: string, locationIdsFingerprint: string): string {
  return `${customerId.trim().toLowerCase()}:${postId.trim().toLowerCase()}:${locationIdsFingerprint}`;
}

function locationIdsFingerprint(locationIds: string[]): string {
  return locationIds.map(id => id.trim()).filter(Boolean).join("|");
}

function invalidate(customerId: string) {
  const prefix = `${customerId.trim().toLowerCase()}:`;
  for (const key of locationCache.keys()) if (key.startsWith(prefix)) locationCache.delete(key);
  for (const key of postLocationDisplayCache.keys()) if (key.startsWith(prefix)) postLocationDisplayCache.delete(key);
}

export default class SocialLocationsRepo {
  static async list(customerId: string, includeDeleted = false, useCache = true): Promise<LocationData[]> {
    const key = customerKey(customerId, includeDeleted);
    if (!useCache || !locationCache.has(key)) {
      locationCache.set(
        key,
        shrpaApi
          .get<LocationData[]>(`/locations/${encodeURIComponent(customerId)}`, { params: { includeDeleted } })
          .then(response => response.data)
          .catch(error => {
            locationCache.delete(key);
            throw error;
          })
      );
    }
    return (await locationCache.get(key)) ?? [];
  }

  static async listTaggable(customerId: string, skipIfNoTags = false): Promise<TaggableLocation[]> {
    const { data } = await shrpaApi.get<{ items: TaggableLocation[] }>(`/locations/${encodeURIComponent(customerId)}/taggable`, {
      params: { skipIfNoTags },
    });
    return data.items;
  }

  static async resolve(customerId: string, locationIds: string[]): Promise<LocationData[]> {
    if (!locationIds.length) return [];
    const { data } = await shrpaApi.post<LocationData[]>(`/locations/${encodeURIComponent(customerId)}/resolve`, { locationIds });
    return data;
  }

  static async getForPost(customerId: string, postId: string, locationIds: string[]): Promise<PostLocationDisplayItem[]> {
    if (!customerId || !postId || !locationIds.length) return [];
    const fingerprint = locationIdsFingerprint(locationIds);
    const key = postLocationCacheKey(customerId, postId, fingerprint);
    if (!postLocationDisplayCache.has(key)) {
      postLocationDisplayCache.set(
        key,
        shrpaApi
          .get<PostLocationDisplayItem[]>(`/locations/${encodeURIComponent(customerId)}/posts/${encodeURIComponent(postId)}`)
          .then(response => response.data)
          .catch(error => {
            postLocationDisplayCache.delete(key);
            throw error;
          })
      );
    }
    return (await postLocationDisplayCache.get(key)) ?? [];
  }

  static async create(customerId: string, request: SaveLocationRequest): Promise<LocationData> {
    const calendarId = await getCalendarId(customerId);
    const { data } = await shrpaApi.post<LocationData>(`/locations/${encodeURIComponent(customerId)}/${encodeURIComponent(calendarId)}`, request);
    invalidate(customerId);
    return data;
  }

  static async update(customerId: string, locationId: string, request: SaveLocationRequest): Promise<LocationData> {
    const calendarId = await getCalendarId(customerId);
    const { data } = await shrpaApi.put<LocationData>(
      `/locations/${encodeURIComponent(customerId)}/${encodeURIComponent(calendarId)}/${encodeURIComponent(locationId)}`,
      request
    );
    invalidate(customerId);
    return data;
  }

  static async archive(customerId: string, locationId: string): Promise<void> {
    await shrpaApi.delete(`/locations/${encodeURIComponent(customerId)}/${encodeURIComponent(locationId)}`);
    invalidate(customerId);
  }

  static async validateInstagram(customerId: string, handle: string, saveIcon: boolean): Promise<InstagramValidationResult> {
    const calendarId = await getCalendarId(customerId);
    const { data } = await shrpaApi.post<InstagramValidationResult>(
      `/social-discovery/${encodeURIComponent(customerId)}/${encodeURIComponent(calendarId)}/instagram/validate`,
      { handle, saveIcon }
    );
    return data;
  }

  static async searchFacebookPages(customerId: string, query: string): Promise<FacebookPageSearchResult[]> {
    const calendarId = await getCalendarId(customerId);
    const { data } = await shrpaApi.get<{ items: FacebookPageSearchResult[] }>(
      `/social-discovery/${encodeURIComponent(customerId)}/${encodeURIComponent(calendarId)}/facebook-pages`,
      { params: { q: query, limit: 10 } }
    );
    return data.items;
  }

  static async resolveFacebookPage(customerId: string, input: string, saveIcon = false): Promise<FacebookPageSearchResult> {
    const calendarId = await getCalendarId(customerId);
    const { data } = await shrpaApi.post<FacebookPageSearchResult>(
      `/social-discovery/${encodeURIComponent(customerId)}/${encodeURIComponent(calendarId)}/facebook-page/resolve`,
      { input, saveIcon }
    );
    return data;
  }

  static async discoverSocialsFromWebsite(customerId: string, websiteUrl: string): Promise<WebsiteSocialDiscoveryResult> {
    const calendarId = await getCalendarId(customerId);
    const { data } = await shrpaApi.post<WebsiteSocialDiscoveryResult>(
      `/social-discovery/${encodeURIComponent(customerId)}/${encodeURIComponent(calendarId)}/website/discover`,
      { websiteUrl }
    );
    return data;
  }

  /** Imports a discovered website apple-touch-icon when Instagram/Facebook did not supply a profile photo. */
  static async importWebsiteIcon(customerId: string, iconUrl: string): Promise<string | null> {
    const calendarId = await getCalendarId(customerId);
    const { data } = await shrpaApi.post<{ iconId?: string | null }>(
      `/social-discovery/${encodeURIComponent(customerId)}/${encodeURIComponent(calendarId)}/website/icon`,
      { iconUrl }
    );
    return data.iconId ?? null;
  }
}
